// Predicate-head keyword/snippet emission (P4, PH-D4/PH-D5). Legality is
// engine-gated here; the emit module calls this once per head.

import type { CompletionCandidate, DatabaseEngineId } from '@/core/engines';
import { pushScalarSubquerySnippet } from '../valueSlot/keywords';
import { KEYWORD_TIER } from '../valueSlot/tiers';

/**
 * TRUE/FALSE at a bare predicate head (PH-D5) — pgsql/mysql/sqlite ONLY.
 *
 * This set is deliberately NARROWER than the value slot's `TRUE_FALSE_ENGINES`
 * and must not be merged with it:
 *   - mssql has no boolean literals at all (`WHERE TRUE` is a syntax error).
 *   - oracle is excluded in v1. The value slot can include oracle because its
 *     `lhsBooleanish` flag IS the version gate — only 23ai+ can declare the
 *     BOOLEAN column that sets it. A bare head has no LHS, so nothing
 *     self-gates the version and a pre-23ai server would be offered a literal
 *     it rejects. Gap-doc'd; revisit if a server-version signal reaches here.
 */
const HEAD_TRUE_FALSE_ENGINES: ReadonlySet<DatabaseEngineId> = new Set([
  'pgsql', 'mysql', 'sqlite',
]);

export function emitPredicateHeadKeywords(
  out: CompletionCandidate[],
  engine: DatabaseEngineId,
): void {
  // EXISTS / NOT EXISTS — a subquery predicate is legal at every head on
  // every supported engine. `filterText` drops the parenthesised tail so
  // typing `exi` still matches (Monaco filters on filterText, not label).
  for (const kw of ['EXISTS', 'NOT EXISTS'] as const) {
    out.push({
      label: `${kw} (SELECT …)`,
      insertText: `${kw} (SELECT $1)`,
      isSnippet: true,
      filterText: kw,
      kind: 'snippet',
      tier: KEYWORD_TIER,
      detail: 'subquery predicate',
    });
  }

  // Bare NOT — negating whatever the user types next.
  out.push({
    label: 'NOT',
    insertText: 'NOT',
    isSnippet: false,
    kind: 'keyword',
    tier: KEYWORD_TIER,
    detail: 'SQL keyword',
  });

  // `(SELECT …)` — shared with the value slot precisely so the candidate
  // shape cannot drift between the two policies.
  pushScalarSubquerySnippet(out);

  if (HEAD_TRUE_FALSE_ENGINES.has(engine)) {
    for (const kw of ['TRUE', 'FALSE'] as const) {
      out.push({
        label: kw,
        insertText: kw,
        isSnippet: false,
        kind: 'keyword',
        tier: KEYWORD_TIER,
        detail: 'boolean literal',
      });
    }
  }
}
